import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Customer, CustomerOverride } from '../../types';
import { fetchCustomer, updateCustomer } from '../../services/crmService';
import { appendChangeEntry } from '../../services/customerChangeLog';
import {
    DocumentCustomerState,
    DocumentKind,
    applyLegacyAddress,
    customerEditNotice,
    legacyAddressHint,
    personNameParts,
    planCustomerUpdate,
    validateCustomerQuickEdit,
    withPersonNamePart,
} from '../../services/customerQuickEdit';
import {
    CustomerEditScope,
    applyCustomerOverride,
    canScopeToDocument,
    customerOverrideApplies,
    customerOverrideFromUpdates,
    customerOverrideSummary,
    defaultCustomerEditScope,
    documentScopeBlockedReason,
    documentScopeLabel,
    documentScopeNotice,
    hasCustomerOverride,
    liftOverrideWarning,
    sameCustomerOverride,
} from '../../services/customerOverride';
import { parseLegacyAddress } from './verifyAddress';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { rechtFehlerOder, rechtFehltHinweis } from '../../services/rechte/rechtFehler';
import { Button } from '../Button';
import {
    AlertTriangle,
    Building2,
    Globe2,
    Loader2,
    Lock,
    MapPin,
    PencilLine,
    RotateCcw,
    User,
    X,
} from 'lucide-react';

/**
 * Kundenstamm aus einem offenen Beleg heraus bearbeiten.
 *
 * Bewusst NICHT die CustomerDetail-Maske im Dialograhmen: die speichert alle
 * zwei Sekunden von selbst. In einem Beleg-Popup hiesse das, dass schon das
 * Ansehen der Daten den Kundenstamm schreibt und ein "Abbrechen" nichts mehr
 * zurueckzunehmen haette. Hier gilt: Uebernehmen speichert, Abbrechen nicht.
 *
 * Der Umfang folgt dem Andruck — genau die Felder, die Rechnung und Angebot aus
 * dem Kundenstamm drucken, plus E-Mail und Telefon fuer den Versand. Alles
 * Weitere (Konditionen, Bewertung, Preise, Rahmenvertraege) bleibt im
 * Kundenstamm; ein Beleg ist nicht der Ort, an dem man Preise pflegt.
 */

const inputClass =
    'w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-0.5';

interface Props {
    customerId: string;
    /**
     * Der am Beleg bekannte Kundenstand — nur, damit der Dialog waehrend des
     * Ladens nicht leer steht. Massgeblich ist immer der frisch geladene: die
     * Liste kann veraltet sein, und in der Angebotsansicht ist sie bei
     * unbekanntem Kunden sogar nur ein Platzhalter mit Namen.
     */
    fallback?: Customer;
    kind: DocumentKind;
    /**
     * Wie der geoeffnete Beleg auf eine Aenderung am KUNDENSTAMM reagiert.
     * Steuert den Hinweis oben und daneben, ob die belegbezogene Wahl
     * ueberhaupt offensteht (nur im offenen Beleg — canScopeToDocument).
     */
    state: DocumentCustomerState;
    /** Die Abweichung, die der Beleg heute fuehrt (details.customerOverride). */
    override?: CustomerOverride;
    /**
     * Uebernimmt der naechste Entwurf desselben Projekts die Abweichung? Nur
     * eine Zusage im Hinweistext — der Beleg selbst weiss, ob er an einem
     * Projekt haengt.
     */
    projectSticky?: boolean;
    /** Global gespeichert: der neue Kundenstamm. */
    onSaved: (updated: Customer) => void;
    /**
     * Belegbezogen gespeichert: die neue Abweichung, `undefined` heisst "keine
     * mehr" (der Beleg folgt wieder dem Kundenstamm).
     *
     * Fehlt der Rueckweg, gibt es die Wahl nicht — der Dialog schreibt dann
     * wie bisher ausschliesslich in den Kundenstamm.
     */
    onSavedForDocument?: (override: CustomerOverride | undefined) => void;
    /**
     * Darf der Kundenstamm geschrieben werden (Rollensystem: „Kunden
     * verwalten“)? Ohne bleibt nur die belegbezogene Reichweite — der Aufrufer
     * oeffnet den Dialog dann nur, solange der Beleg sie aufnimmt. Ohne Angabe
     * wie bisher: ja.
     */
    stammAenderbar?: boolean;
    onClose: () => void;
}

export const CustomerQuickEditModal: React.FC<Props> = ({
    customerId,
    fallback,
    kind,
    state,
    override,
    projectSticky,
    onSaved,
    onSavedForDocument,
    stammAenderbar = true,
    onClose,
}) => {
    /**
     * Der geladene Serverstand des KUNDENSTAMMS — Diffbasis beim Speichern,
     * und zwar fuer beide Reichweiten. Die belegbezogene Abweichung ist ein
     * Diff gegen genau diesen Stand: was mit dem Stamm uebereinstimmt, ist
     * keine Abweichung und faellt beim Speichern von selbst heraus.
     */
    const [loaded, setLoaded] = useState<Customer | null>(null);
    /**
     * Die Abweichung, soweit sie fuer DIESEN Beleg ueberhaupt noch gilt.
     *
     * Im festgeschriebenen Beleg gilt allein der customerSnapshot — die
     * Abweichung steckt dort laengst eingerechnet drin. Sie hier trotzdem
     * anzuzeigen, zu bearbeiten oder zu "heben" waere eine Bedienung ohne
     * Wirkung; der Dialog ist dann wieder genau das, was er vorher war: eine
     * Maske auf den Kundenstamm.
     *
     * Gefragt wird ausdruecklich customerOverrideApplies und nicht
     * canScopeToDocument: der Dialog soll zeigen, was der Beleg TATSAECHLICH
     * druckt. Beide Antworten fallen heute zusammen — und genau deshalb muessen
     * die Masken dieselbe Funktion fragen, sonst blendet der Dialog aus, was die
     * Vorschau daneben weiter ausgibt.
     */
    const activeOverride = customerOverrideApplies(state) ? override : undefined;

    /**
     * Derselbe Stand, aber eingefroren auf das Oeffnen des Dialogs.
     *
     * Der Ladeeffekt haengt allein an der Kunden-Id: er darf nicht erneut
     * laufen, nur weil der Beleg im Hintergrund gespeichert hat — das holte
     * den Kunden ein zweites Mal und setzte das gerade Getippte zurueck.
     */
    const overrideAtOpen = useRef(activeOverride);

    /**
     * Bearbeitet wird, was der BELEG zeigt — Stamm ueberlagert mit seiner
     * Abweichung. Alles andere waere irrefuehrend: der Dialog stuende auf der
     * Anschrift des Stamms, waehrend das PDF daneben die abweichende druckt.
     */
    const [draft, setDraft] = useState<Customer | null>(
        fallback ? applyCustomerOverride(fallback, activeOverride) : null,
    );
    /**
     * Wohin die Aenderung geht. Vorbelegt mit dem Zustand, in dem der Beleg
     * gerade ist (defaultCustomerEditScope) — fuehrt er bereits eine
     * Abweichung, waere "Kundenstamm" ein unachtsames Anheben in den Stamm.
     */
    const [scope, setScope] = useState<CustomerEditScope>(() =>
        !stammAenderbar && onSavedForDocument
            ? 'document'
            : onSavedForDocument ? defaultCustomerEditScope(state, activeOverride) : 'global',
    );
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setLoadError(null);
        void fetchCustomer(customerId)
            .then((c) => {
                if (!alive) return;
                if (!c) {
                    setLoadError('Der Kunde ist nicht (mehr) vorhanden.');
                    return;
                }
                setLoaded(c);
                setDraft(applyCustomerOverride(c, overrideAtOpen.current));
            })
            .catch((e) => {
                if (alive) setLoadError(describeError(e));
            })
            .finally(() => {
                if (alive) setLoading(false);
            });
        return () => {
            alive = false;
        };
    }, [customerId]);

    const set = <K extends keyof Customer>(key: K, val: Customer[K]) => {
        setDraft((d) => (d ? { ...d, [key]: val } : d));
        setSaveError(null);
    };

    const setNamePart = (key: 'firstName' | 'lastName', val: string) => {
        setDraft((d) => (d ? withPersonNamePart(d, key, val) : d));
        setSaveError(null);
    };

    /**
     * Alt-Anschrift aus dem Freitextfeld uebernehmen.
     *
     * Der Altbestand traegt die Anschrift nur dort. Wer hier bloss die Strasse
     * eintippt, macht aus "Hauptstr. 1, 12345 Berlin" beim Speichern ein
     * nacktes "Nebenweg 5" — PLZ und Ort waeren weg. Deshalb steht das Angebot,
     * solange noch ein Feld leer ist.
     */
    const takeLegacyAddress = () => {
        setDraft((d) => (d ? applyLegacyAddress(d, parseLegacyAddress(d.address || '')) : d));
        setSaveError(null);
    };

    const legacyAddress = loaded && draft ? legacyAddressHint(loaded, draft) : null;

    const isPerson = (draft?.customerType ?? 'company') === 'person';
    const parts = useMemo(
        () => (draft ? personNameParts(draft) : { firstName: '', lastName: '' }),
        [draft],
    );

    /** Ohne geladenen Serverstand wird nicht gespeichert — sonst fehlt die Diffbasis. */
    const plan = useMemo(
        () => (loaded && draft ? planCustomerUpdate(loaded, draft) : null),
        [loaded, draft],
    );

    /**
     * Die Abweichung, die beim belegbezogenen Speichern entstuende.
     *
     * Aus DEMSELBEN Aenderungssatz wie das globale Speichern — eine zweite
     * Vergleichsformel daneben koennte ihm widersprechen. `undefined` heisst:
     * der Beleg weicht nicht mehr ab und folgt wieder dem Kundenstamm.
     */
    const nextOverride = useMemo(
        () => (plan ? customerOverrideFromUpdates(plan.updates) : undefined),
        [plan],
    );

    /** Steht die belegbezogene Wahl offen? */
    const documentScopeOpen = Boolean(onSavedForDocument) && canScopeToDocument(state);
    const documentBlocked = onSavedForDocument ? documentScopeBlockedReason(kind, state) : null;
    const hasOverride = hasCustomerOverride(activeOverride);

    /**
     * Gibt es ueberhaupt etwas zu speichern? Die Frage haengt an der
     * Reichweite: global zaehlt der Unterschied zum Kundenstamm, belegbezogen
     * der Unterschied zur bisherigen Abweichung. Wer eine Abweichung
     * zuruecknimmt, aendert am Stamm nichts — und muss trotzdem speichern
     * koennen.
     */
    const globalDirty = !!plan && Object.keys(plan.updates).length > 0;
    const documentDirty = !sameCustomerOverride(nextOverride, activeOverride);
    const dirty = scope === 'document' ? documentDirty : globalDirty;

    const problems = draft ? validateCustomerQuickEdit(draft) : [];

    const handleClose = () => {
        if ((globalDirty || documentDirty) && !confirm('Die Änderungen am Kunden verwerfen?')) return;
        onClose();
    };

    /**
     * Zurueck auf den Kundenstamm — die Felder zeigen wieder den Stamm, und
     * belegbezogen gespeichert entfaellt damit die Abweichung.
     *
     * Bewusst nur ein Zuruecksetzen der Maske und kein Sofort-Speichern: der
     * Bearbeiter soll sehen, worauf der Beleg zurueckfaellt, bevor er es
     * uebernimmt.
     */
    const resetToMaster = () => {
        if (!loaded) return;
        setDraft(loaded);
        setScope('document');
        setSaveError(null);
    };

    const handleApply = async () => {
        if (!plan || !loaded || problems.length) return;
        // Nichts geaendert: kein Schreibvorgang, kein Historien-Eintrag.
        if (!dirty) {
            onClose();
            return;
        }
        // Belegbezogen: der Kundenstamm wird NICHT angefasst — kein Schreiben
        // auf customers, kein Historien-Eintrag. Der Beleg speichert die
        // Abweichung ueber seinen eigenen Weg (Autosave der Maske).
        if (scope === 'document') {
            onSavedForDocument?.(nextOverride);
            onClose();
            return;
        }
        // Zweite Reihe: der Knopf „Kundenstamm“ ist ohne Recht gesperrt.
        if (!stammAenderbar) {
            setSaveError(rechtFehltHinweis('kunden.verwalten'));
            return;
        }
        setSaving(true);
        setSaveError(null);
        try {
            const { toSave, updates, changes } = plan;
            if (changes.length) {
                const { data: auth } = await supabase.auth.getUser();
                const log = appendChangeEntry(
                    loaded.changeLog ?? [],
                    changes,
                    new Date().toISOString(),
                    auth.user?.email ?? undefined,
                );
                updates.changeLog = log;
                toSave.changeLog = log;
            }
            await updateCustomer(loaded.id, updates);
            onSaved(toSave);
            // Global gespeichert heisst: diese Werte SIND jetzt Stammdaten. Eine
            // daneben stehende Abweichung wuerde dieselben Werte ein zweites
            // Mal fuehren und beim naechsten Umzug des Kunden still veralten —
            // deshalb faellt sie hier weg. Der Hinweis oben sagt das vorher zu.
            if (hasOverride) onSavedForDocument?.(undefined);
            onClose();
        } catch (e) {
            // Der Dialog bleibt offen: die Eingaben gehen nicht verloren, und
            // ein Rechtefehler (customers ist admin-only) ist hier lesbar statt
            // in einem rohen alert().
            setSaveError(rechtFehlerOder(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
            <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-scale-in">
                <div className="flex items-center justify-between bg-blue-600 p-5 text-white">
                    <h3 className="flex items-center gap-2 text-lg font-bold">
                        {isPerson ? <User className="h-5 w-5" /> : <Building2 className="h-5 w-5" />}
                        Kundendaten bearbeiten
                    </h3>
                    <button onClick={handleClose} className="rounded p-1 hover:bg-white/10">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Die REICHWEITE steht vor allem anderen: sie entscheidet, was
                    die Eingaben darunter ueberhaupt bedeuten. Zwei Knoepfe und
                    kein Auswahlfeld — es sind genau zwei Moeglichkeiten, und
                    beide muessen mit ihrer Folge sichtbar sein statt erst nach
                    dem Aufklappen. */}
                {onSavedForDocument && (
                    <div className="border-b border-slate-200 bg-slate-50 px-5 py-3">
                        <span className={`${labelClass} mb-2 block`}>Gilt für</span>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <button
                                type="button"
                                onClick={() => stammAenderbar && setScope('global')}
                                disabled={!stammAenderbar}
                                title={stammAenderbar ? undefined : rechtFehltHinweis('kunden.verwalten')}
                                className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                                    scope === 'global'
                                        ? 'border-blue-500 bg-white ring-2 ring-blue-500/20'
                                        : 'border-slate-200 bg-white hover:border-slate-300 disabled:hover:border-slate-200'
                                }`}
                            >
                                <Globe2
                                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                                        scope === 'global' ? 'text-blue-600' : 'text-slate-400'
                                    }`}
                                />
                                <span>
                                    <span className="block text-sm font-bold text-slate-800">
                                        Kundenstamm
                                    </span>
                                    <span className="block text-[11px] leading-snug text-slate-500">
                                        {stammAenderbar
                                            ? 'Gilt für alle künftigen Belege dieses Kunden.'
                                            : `${rechtFehltHinweis('kunden.verwalten')}.`}
                                    </span>
                                </span>
                            </button>
                            <button
                                type="button"
                                onClick={() => documentScopeOpen && setScope('document')}
                                disabled={!documentScopeOpen}
                                title={documentBlocked ?? undefined}
                                className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                                    scope === 'document'
                                        ? 'border-blue-500 bg-white ring-2 ring-blue-500/20'
                                        : 'border-slate-200 bg-white hover:border-slate-300 disabled:hover:border-slate-200'
                                }`}
                            >
                                <PencilLine
                                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                                        scope === 'document' ? 'text-blue-600' : 'text-slate-400'
                                    }`}
                                />
                                <span>
                                    <span className="block text-sm font-bold text-slate-800">
                                        {documentScopeLabel(kind)}
                                    </span>
                                    <span className="block text-[11px] leading-snug text-slate-500">
                                        {documentBlocked ?? 'Der Kundenstamm bleibt unverändert.'}
                                    </span>
                                </span>
                            </button>
                        </div>
                        {hasOverride && (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                <span>
                                    Dieser Beleg weicht bereits ab:{' '}
                                    <span className="font-bold text-slate-700">
                                        {customerOverrideSummary(activeOverride)}
                                    </span>
                                </span>
                                {documentScopeOpen && (
                                    <button
                                        type="button"
                                        onClick={resetToMaster}
                                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 font-bold text-slate-600 transition-all hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                    >
                                        <RotateCcw className="h-3 w-3" />
                                        Abweichung entfernen
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Die Zusage ueber DIESEN Beleg steht vor den Feldern, nicht
                    hinter dem Speichern-Knopf: sie entscheidet, ob der Nutzer
                    hier ueberhaupt richtig ist. */}
                <div
                    className={`border-b px-5 py-3 text-sm ${
                        scope === 'document'
                            ? 'border-blue-100 bg-blue-50 text-blue-900'
                            : hasOverride || state === 'frozen'
                              ? 'border-amber-200 bg-amber-50 text-amber-900'
                              : state === 'unfrozen'
                                ? 'border-red-200 bg-red-50 text-red-800'
                                : 'border-blue-100 bg-blue-50 text-blue-900'
                    }`}
                >
                    <span className="flex items-start gap-2">
                        {state === 'frozen' && scope === 'global' && !hasOverride ? (
                            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                        ) : (
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        )}
                        <span>
                            {scope === 'document'
                                ? documentScopeNotice(kind, Boolean(projectSticky))
                                : hasOverride
                                  ? liftOverrideWarning(kind)
                                  : customerEditNotice(kind, state)}
                        </span>
                    </span>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto p-5">
                    {loading && (
                        <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
                            <Loader2 className="h-4 w-4 animate-spin" /> Kundendaten werden geladen…
                        </div>
                    )}
                    {loadError && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            Der Kundenstamm konnte nicht geladen werden: {loadError}
                        </div>
                    )}

                    {draft && !loading && !loadError && (
                        <>
                            {/* Kundennummer bleibt gesperrt: sie ist die
                                Debitorennummer (DATEV-Nummernkreis, Vergabe per
                                next_customer_number) und steht auf allen bereits
                                ausgestellten Belegen. Sie im Vorbeigehen aus
                                einem Beleg heraus zu aendern, laesst sich
                                nirgends mehr zuruecknehmen — dafuer ist der
                                Kundenstamm der richtige Ort. */}
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[200px_1fr]">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Kundennummer</label>
                                    <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 font-mono text-sm text-slate-600">
                                        <Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                                        {draft.customerNumber || '—'}
                                    </div>
                                    <p className="ml-0.5 text-[11px] text-slate-400">
                                        Nur im Kundenstamm änderbar
                                    </p>
                                </div>
                                {isPerson ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className={labelClass}>Anrede</label>
                                            <select
                                                value={draft.salutation || ''}
                                                onChange={(e) => set('salutation', e.target.value)}
                                                className={inputClass}
                                            >
                                                <option value="">—</option>
                                                <option value="Herr">Herr</option>
                                                <option value="Frau">Frau</option>
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className={labelClass}>Titel</label>
                                            <input
                                                value={draft.title || ''}
                                                onChange={(e) => set('title', e.target.value)}
                                                className={inputClass}
                                                placeholder="Dr."
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-1.5">
                                        <label className={labelClass}>Name 1 (Zeile 1) *</label>
                                        <input
                                            value={draft.name || ''}
                                            onChange={(e) => set('name', e.target.value)}
                                            className={`${inputClass} font-bold`}
                                            placeholder="Firmenname"
                                        />
                                    </div>
                                )}
                            </div>

                            {isPerson ? (
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <label className={labelClass}>Vorname</label>
                                        <input
                                            value={parts.firstName}
                                            onChange={(e) => setNamePart('firstName', e.target.value)}
                                            className={inputClass}
                                            placeholder="Vorname"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className={labelClass}>Nachname *</label>
                                        <input
                                            value={parts.lastName}
                                            onChange={(e) => setNamePart('lastName', e.target.value)}
                                            className={`${inputClass} font-bold`}
                                            placeholder="Nachname"
                                        />
                                    </div>
                                </div>
                            ) : (
                                /* Zeile 2 und 3 sind reiner Adress-Andruck —
                                   Suche, Listen und DATEV kennen nur name. */
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className="space-y-1.5">
                                        <label className={labelClass}>Name 2 (Zeile 2)</label>
                                        <input
                                            value={draft.name2 || ''}
                                            onChange={(e) => set('name2', e.target.value)}
                                            className={inputClass}
                                            placeholder="z. B. Geschäftsbereich"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className={labelClass}>Name 3 (Zeile 3)</label>
                                        <input
                                            value={draft.name3 || ''}
                                            onChange={(e) => set('name3', e.target.value)}
                                            className={inputClass}
                                            placeholder="z. B. z. Hd. …"
                                        />
                                    </div>
                                </div>
                            )}

                            {legacyAddress && (
                                <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
                                    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                                    <span className="flex-1">Alt: {legacyAddress}</span>
                                    <button
                                        type="button"
                                        onClick={takeLegacyAddress}
                                        className="shrink-0 rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-bold text-slate-600 transition-all hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                                    >
                                        In die Felder übernehmen
                                    </button>
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_2fr]">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Straße & Nr.</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-300" />
                                        <input
                                            value={draft.street || ''}
                                            onChange={(e) => set('street', e.target.value)}
                                            className={`${inputClass} pl-9`}
                                            placeholder="Musterstraße 1"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>PLZ</label>
                                    <input
                                        value={draft.zip || ''}
                                        onChange={(e) => set('zip', e.target.value)}
                                        className={inputClass}
                                        placeholder="12345"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Ort</label>
                                    <input
                                        value={draft.city || ''}
                                        onChange={(e) => set('city', e.target.value)}
                                        className={inputClass}
                                        placeholder="Musterstadt"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="space-y-1.5">
                                    <label className={labelClass}>E-Mail</label>
                                    <input
                                        value={draft.email || ''}
                                        onChange={(e) => set('email', e.target.value)}
                                        className={inputClass}
                                        placeholder="rechnung@kunde.de"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className={labelClass}>Telefon</label>
                                    <input
                                        value={draft.phone || ''}
                                        onChange={(e) => set('phone', e.target.value)}
                                        className={inputClass}
                                        placeholder="030 1234567"
                                    />
                                </div>
                            </div>

                            {problems.length > 0 && (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    {problems.map((p) => (
                                        <div key={p}>{p}</div>
                                    ))}
                                </div>
                            )}
                            {saveError && (
                                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    Speichern fehlgeschlagen: {saveError}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 border-t bg-slate-50 p-4">
                    <span className="text-xs text-slate-400">
                        {scope === 'document'
                            ? 'Nur der Andruck weicht ab — Kundennummer, Konditionen und Preise bleiben die des Kundenstamms.'
                            : 'Weitere Felder (Konditionen, Preise, Bewertung) im Kundenstamm.'}
                    </span>
                    <div className="flex gap-3">
                        <Button variant="ghost" onClick={handleClose} disabled={saving}>
                            Abbrechen
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleApply}
                            loading={saving}
                            disabled={loading || !!loadError || !loaded || problems.length > 0}
                        >
                            {saving
                                ? 'Speichert…'
                                : scope === 'document'
                                  ? 'Nur für diesen Beleg speichern'
                                  : 'In Kundenstamm speichern'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
